import { useState, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Theme } from '../lib/tokens';
import { FONTS } from '../lib/tokens';
import { Icon } from '../components/icons';
import { Img } from '../components/Img';
import { useChat } from '../hooks/useChat';
import { useAuth } from '../hooks/useAuth';
import { DateSheet } from '../sheets/DateSheet';
import { ReportBlockSheet } from '../sheets/ReportBlockSheet';
import type { MatchInfo } from './Discover';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';

interface ChatScreenProps {
  t: Theme;
  chatRoomId: string;
  matchId?: string;
  hasDatePlan?: boolean;
  otherUserName: string;
  otherUserPhoto: string | null;
  matchInfo?: MatchInfo;
  // Present for singles chats (one other person). Enables report/block from the
  // chat header. Omitted for couples chats (4-person — ambiguous who to report).
  reportTargetId?: string;
  onBack: () => void;
  onOpenDatePlan?: () => void;
  onViewProfile?: () => void;
}

export function ChatScreen({ t, chatRoomId, matchId, hasDatePlan, otherUserName, otherUserPhoto, matchInfo, reportTargetId, onBack, onOpenDatePlan, onViewProfile }: ChatScreenProps) {
  const { userProfile, getIdToken } = useAuth();
  const queryClient = useQueryClient();
  const { messages, loading, sendMessage, roomInfo } = useChat(chatRoomId);
  const isCouplesChat = roomInfo?.chatType === 'couples';
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [showDateSheet, setShowDateSheet] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [generatedPlan, setGeneratedPlan] = useState<any>(null);

  const generatePlanMutation = useMutation({
    mutationFn: async () => {
      console.log('[DatePlan] mutationFn fired, matchId=', matchId);
      if (!matchId) throw new Error('No pair ID');
      const token = await getIdToken();
      console.log('[DatePlan] got token, fetching...');
      const res = await fetch(`${API_BASE}/api/matches/${matchId}/date-plan`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await res.json();
      console.log('[DatePlan] response', res.status, body);
      if (!res.ok) throw new Error(body.error || 'Failed');
      return body;
    },
    onSuccess: (data) => {
      console.log('[DatePlan] success', data);
      setGeneratedPlan(data.datePlan);
      queryClient.invalidateQueries({ queryKey: ['matches'] });
      queryClient.invalidateQueries({ queryKey: ['match-plan'] });
      setShowDateSheet(true);
    },
    onError: (err) => console.error('[DatePlan] error', err),
  });
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    setDraft('');
    inputRef.current?.focus();
    try {
      await sendMessage(text);
    } catch {
      setDraft(text); // restore on failure
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const myId = userProfile?.id;

  return (
    <div style={{
      position: 'absolute', inset: 0, zIndex: 50,
      display: 'flex', flexDirection: 'column',
      background: t.paper,
    }}>
      {showDateSheet && matchInfo && (
        <DateSheet
          t={t}
          match={matchInfo}
          initialPlan={generatedPlan}
          onClose={() => setShowDateSheet(false)}
        />
      )}
      {showReport && reportTargetId && (
        <ReportBlockSheet
          t={t}
          targetUserId={reportTargetId}
          targetName={otherUserName}
          onClose={() => setShowReport(false)}
          onDone={onBack}
        />
      )}
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 16px', borderBottom: `1px solid ${t.line}`,
        flexShrink: 0,
      }}>
        <button onClick={onBack} className="pm-press" style={{
          all: 'unset', cursor: 'pointer', width: 36, height: 36,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: '50%', background: t.paperAlt,
        }}>
          {Icon.back({ s: 18, c: t.ink })}
        </button>
        <button onClick={onViewProfile} style={{ all: 'unset', display: 'flex', alignItems: 'center', gap: 10, flex: 1, cursor: onViewProfile ? 'pointer' : 'default', minWidth: 0 }}>
          {otherUserPhoto
            ? <Img placeholder={t.paperAlt} src={otherUserPhoto} alt="" style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
            : <div style={{ width: 38, height: 38, borderRadius: '50%', background: t.paperAlt, flexShrink: 0 }} />}
          <span style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 20, color: t.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {otherUserName}
          </span>
        </button>
        {matchId && (
          hasDatePlan
            ? (
              <button onClick={() => matchInfo ? setShowDateSheet(true) : onOpenDatePlan?.()} style={{
                all: 'unset', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '6px 10px', borderRadius: 100,
                border: `1px solid ${t.terracotta}`,
                fontFamily: FONTS.sans, fontSize: 11, fontWeight: 600, color: t.terracotta,
              }}>
                {Icon.sparkle({ s: 11, c: t.terracotta })} Plan
              </button>
            )
            : (
              <button onClick={() => { console.log('[DatePlan] button clicked, matchId=', matchId, 'hasDatePlan=', hasDatePlan); generatePlanMutation.mutate(); }} disabled={generatePlanMutation.isPending} style={{
                all: 'unset', cursor: generatePlanMutation.isPending ? 'default' : 'pointer',
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '6px 10px', borderRadius: 100,
                border: `1px dashed ${t.line}`,
                fontFamily: FONTS.sans, fontSize: 11, fontWeight: 600,
                color: generatePlanMutation.isPending ? t.inkMute : t.ink,
              }}>
                {generatePlanMutation.isPending
                  ? <div style={{ width: 11, height: 11, borderRadius: '50%', border: `1.5px solid ${t.terracotta}`, borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
                  : Icon.sparkle({ s: 11, c: t.inkMute })}
                {generatePlanMutation.isPending ? 'Thinking...' : 'Plan date'}
              </button>
            )
        )}
        {reportTargetId && (
          <button onClick={() => setShowReport(true)} aria-label="Report or block" style={{
            all: 'unset', cursor: 'pointer', width: 34, height: 34, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: '50%', background: t.paperAlt,
          }}>
            {Icon.more({ s: 18, c: t.ink })}
          </button>
        )}
      </div>

      {/* Disclaimer */}
      <div style={{
        flexShrink: 0, padding: '8px 16px',
        background: t.paperAlt, borderBottom: `1px solid ${t.line}`,
        fontFamily: FONTS.sans, fontSize: 11.5, color: t.inkMute,
        lineHeight: 1.45, textAlign: 'center',
      }}>
        💬 This chat is for logistics. The whole point is to meet to get to know each other. 🙂{' '}
        <em>Future versions will only make this available 6 hours before the meet.</em>
      </div>

      {/* Messages */}
      {/* Masked: private messages must never appear in Clarity recordings. */}
      <div data-clarity-mask="true" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 16px' }} className="scroll-container">
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
            <div style={{ width: 24, height: 24, borderRadius: '50%', border: `2px solid ${t.terracotta}`, borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
          </div>
        )}

        {!loading && messages.map(msg => {
          const isMe = msg.senderId === myId;
          const isSystem = msg.type === 'system' || msg.type === 'spark_reveal' || msg.senderId === 'system';

          if (isSystem) {
            return (
              <div key={msg.id} style={{ textAlign: 'center', margin: '12px 0' }}>
                <span style={{
                  display: 'inline-block', padding: '6px 14px', borderRadius: 100,
                  background: t.paperAlt, border: `1px solid ${t.line}`,
                  fontFamily: FONTS.sans, fontSize: 11.5, color: t.inkMute,
                  whiteSpace: 'pre-wrap', textAlign: 'left', maxWidth: 280,
                }}>
                  {msg.text}
                </span>
              </div>
            );
          }

          return (
            <div key={msg.id} style={{
              display: 'flex', flexDirection: 'column',
              alignItems: isMe ? 'flex-end' : 'flex-start',
              marginBottom: 8,
            }}>
              {isCouplesChat && !isMe && (
                <div style={{
                  fontFamily: FONTS.sans, fontSize: 10.5, fontWeight: 600,
                  color: t.gold, marginBottom: 3, paddingLeft: 4,
                }}>
                  {msg.senderName}
                </div>
              )}
              <div style={{
                maxWidth: '72%', padding: '10px 14px', borderRadius: isMe ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                background: isMe ? t.terracotta : t.card,
                border: isMe ? 'none' : `1px solid ${t.line}`,
                boxShadow: t.shadow,
              }}>
                <div style={{
                  fontFamily: FONTS.sans, fontSize: 14, lineHeight: 1.45,
                  color: isMe ? '#fff' : t.ink,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}>
                  {msg.text}
                </div>
                <div style={{
                  fontFamily: FONTS.mono, fontSize: 9, marginTop: 4,
                  color: isMe ? 'rgba(255,255,255,0.6)' : t.inkMute,
                  textAlign: 'right',
                }}>
                  {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{
        flexShrink: 0, padding: '10px 16px 16px',
        borderTop: `1px solid ${t.line}`,
        display: 'flex', alignItems: 'flex-end', gap: 10,
        background: t.paper,
      }}>
        <textarea
          ref={inputRef}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message…"
          rows={1}
          style={{
            flex: 1, resize: 'none', border: `1.5px solid ${t.line}`,
            borderRadius: 22, padding: '10px 16px',
            fontFamily: FONTS.sans, fontSize: 14, color: t.ink,
            background: t.card, outline: 'none',
            lineHeight: 1.4, maxHeight: 120, overflowY: 'auto',
          }}
        />
        <button
          className="pm-press"
          onClick={handleSend}
          disabled={!draft.trim() || sending}
          style={{
            all: 'unset', cursor: draft.trim() && !sending ? 'pointer' : 'default',
            width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
            background: draft.trim() && !sending ? t.terracotta : t.line,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 150ms',
          }}
        >
          {Icon.arrow({ s: 18, c: '#fff' })}
        </button>
      </div>
    </div>
  );
}
